// Per-step editor drawer (spec 01 §4 S4). Holds a local copy of the step being
// added/edited; applies back to the list on save after client-side validation
// (§3.5 rules mirrored in model.validateStep — the server re-validates on
// activate). All labels are i18n; numbers parse defensively.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Banner } from "../../../../../components/toast";
import { InstructionsEditor } from "./instructions-editor";
import {
  distinctSetupKeys,
  validateStep,
  workCenterLabel,
  type InstructionsDoc,
  type RunBasis,
  type StepDraft,
  type StepFieldError,
  type WorkCenter,
} from "./model";

function numOr(value: string, fallback: number): number {
  if (value.trim() === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function StepDrawer({
  step,
  mode,
  workCenters,
  wcLoading,
  wcError,
  setupKeyPool,
  readOnly,
  onApply,
  onCancel,
  onDelete,
}: {
  step: StepDraft;
  mode: "new" | "edit";
  workCenters: readonly WorkCenter[];
  wcLoading: boolean;
  wcError: boolean;
  setupKeyPool: readonly StepDraft[];
  readOnly: boolean;
  onApply: (next: StepDraft) => void;
  onCancel: () => void;
  onDelete: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<StepDraft>(step);
  const [showErrors, setShowErrors] = useState(false);

  function patch(partial: Partial<StepDraft>): void {
    setDraft((d) => ({ ...d, ...partial }));
  }

  const errors = validateStep(draft);
  const hasError = (e: StepFieldError): boolean => showErrors && errors.includes(e);

  const datalistId = "rte-setup-keys";
  const keySuggestions = distinctSetupKeys(setupKeyPool);
  const lang = i18n.language;

  function handleApply(): void {
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    onApply(draft);
  }

  function setPrimaryWc(wcId: string): void {
    patch({
      work_center_id: wcId,
      alternate_work_center_ids: draft.alternate_work_center_ids.filter((x) => x !== wcId),
    });
  }

  function toggleAlternate(wcId: string): void {
    setDraft((d) => {
      const has = d.alternate_work_center_ids.includes(wcId);
      return {
        ...d,
        alternate_work_center_ids: has
          ? d.alternate_work_center_ids.filter((x) => x !== wcId)
          : [...d.alternate_work_center_ids, wcId],
      };
    });
  }

  /* A read-only drawer is not an EDIT drawer. It used to be titled "Edit step
   * 10" on an ACTIVE routing whose every field discards keystrokes, which
   * promises an action the surface cannot perform. Now it says what it is, and
   * the banner below says why. */
  const title =
    mode === "new"
      ? t("catalogue.routing.drawer.titleNew")
      : readOnly
        ? t("catalogue.routing.drawer.titleView", { n: draft.step_no })
        : t("catalogue.routing.drawer.titleEdit", { n: draft.step_no });

  return (
    <div className="cat-scrim cat-scrim-end" role="presentation" onClick={onCancel}>
      <aside
        className="cat-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="step-drawer"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header className="cat-modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="ds-iconbtn cat-row-end"
            aria-label={t("catalogue.routing.drawer.close")}
            onClick={onCancel}
          >
            ✕
          </button>
        </header>

        <div className="cat-modal-body">
          {/* Say WHY nothing here can be changed, once, at the top — the same
              §3.8 `Banner` primitive the routing screen already uses for its
              own read-only state, not a fourth hand-rolled rendering of the
              idea. Without it the only signal was the absence of a Save. */}
          {readOnly ? (
            <Banner kind="neutral" data-testid="step-drawer-readonly">
              {t("catalogue.routing.drawer.readOnly")}
            </Banner>
          ) : null}
          <label className="rte-field">
            <span>{t("catalogue.routing.fields.name")}</span>
            <input
              type="text"
              value={draft.name}
              readOnly={readOnly}
              placeholder={t("catalogue.routing.fields.namePlaceholder")}
              onChange={(e) => {
                patch({ name: e.target.value });
              }}
            />
            {hasError("nameRequired") ? (
              <span className="rte-field-error">{t("catalogue.routing.errors.nameRequired")}</span>
            ) : null}
          </label>

          <label className="rte-field">
            <span>{t("catalogue.routing.fields.workCenter")}</span>
            {/* TODO(module-03): §4 S4 specifies a "current load sparkline from 03"
                next to each work-center option. Module 03 (Planning) does not
                exist yet, so there is no capacity/load feed to source it from. Add
                the sparkline when module 03 lands (spec 01 §12); do NOT fake load
                data here. */}
            {wcLoading ? (
              <span className="rte-muted">{t("catalogue.routing.workCenters.loading")}</span>
            ) : wcError ? (
              <span className="rte-field-error">{t("catalogue.routing.workCenters.error")}</span>
            ) : workCenters.length === 0 ? (
              <span className="rte-muted">{t("catalogue.routing.workCenters.empty")}</span>
            ) : (
              <select
                value={draft.work_center_id}
                disabled={readOnly}
                onChange={(e) => {
                  setPrimaryWc(e.target.value);
                }}
              >
                <option value="" disabled>
                  {t("catalogue.routing.workCenters.placeholder")}
                </option>
                {workCenters.map((wc) => (
                  <option value={wc.id} key={wc.id}>
                    {wc.code} · {workCenterLabel(wc, lang)}
                  </option>
                ))}
              </select>
            )}
            {hasError("workCenterRequired") ? (
              <span className="rte-field-error">
                {t("catalogue.routing.errors.workCenterRequired")}
              </span>
            ) : null}
            <span className="rte-hint">{t("catalogue.routing.workCenters.loadHint")}</span>
          </label>

          <div className="rte-field-row">
            <label className="rte-field">
              <span>{t("catalogue.routing.fields.setupMinutes")}</span>
              <input
                type="number"
                min={0}
                step="any"
                value={String(draft.setup_minutes)}
                readOnly={readOnly}
                onChange={(e) => {
                  patch({ setup_minutes: numOr(e.target.value, 0) });
                }}
              />
            </label>
            <label className="rte-field">
              <span>{t("catalogue.routing.fields.runBasis")}</span>
              <select
                value={draft.run_basis}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ run_basis: e.target.value as RunBasis });
                }}
              >
                <option value="per_unit">{t("catalogue.routing.runBasis.per_unit")}</option>
                <option value="per_batch">{t("catalogue.routing.runBasis.per_batch")}</option>
              </select>
            </label>
          </div>

          <div className="rte-field-row">
            <label className="rte-field">
              <span>
                {draft.run_basis === "per_batch"
                  ? t("catalogue.routing.fields.runMinutesBatch")
                  : t("catalogue.routing.fields.runMinutesUnit")}
              </span>
              <input
                type="number"
                min={0}
                step="any"
                value={String(draft.run_minutes)}
                readOnly={readOnly}
                onChange={(e) => {
                  patch({ run_minutes: numOr(e.target.value, 0) });
                }}
              />
              {hasError("runMinutesInvalid") ? (
                <span className="rte-field-error">
                  {t("catalogue.routing.errors.runMinutesInvalid")}
                </span>
              ) : null}
            </label>
            {draft.run_basis === "per_batch" ? (
              <label className="rte-field">
                <span>{t("catalogue.routing.fields.batchQty")}</span>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={draft.batch_qty === null ? "" : String(draft.batch_qty)}
                  readOnly={readOnly}
                  onChange={(e) => {
                    patch({
                      batch_qty: e.target.value.trim() === "" ? null : numOr(e.target.value, 0),
                    });
                  }}
                />
                {hasError("batchQtyRequired") ? (
                  <span className="rte-field-error">
                    {t("catalogue.routing.errors.batchQtyRequired")}
                  </span>
                ) : null}
              </label>
            ) : null}
          </div>

          <label className="rte-field">
            <span>{t("catalogue.routing.fields.setupKey")}</span>
            <input
              type="text"
              list={datalistId}
              value={draft.setup_key ?? ""}
              readOnly={readOnly}
              placeholder={t("catalogue.routing.fields.setupKeyPlaceholder")}
              onChange={(e) => {
                patch({ setup_key: e.target.value === "" ? null : e.target.value });
              }}
            />
            <datalist id={datalistId}>
              {keySuggestions.map((k) => (
                <option value={k} key={k} />
              ))}
            </datalist>
            <span className="rte-hint">{t("catalogue.routing.fields.setupKeyHint")}</span>
          </label>

          <label className="rte-field">
            <span>{t("catalogue.routing.fields.operationScrapPct")}</span>
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              value={String(draft.operation_scrap_pct)}
              readOnly={readOnly}
              onChange={(e) => {
                patch({ operation_scrap_pct: numOr(e.target.value, 0) });
              }}
            />
          </label>

          <fieldset className="rte-field rte-alternates">
            <legend>{t("catalogue.routing.fields.alternateWorkCenters")}</legend>
            {workCenters.filter((wc) => wc.id !== draft.work_center_id).length === 0 ? (
              <span className="rte-muted">{t("catalogue.routing.workCenters.noAlternates")}</span>
            ) : (
              <div className="rte-checkbox-list">
                {workCenters
                  .filter((wc) => wc.id !== draft.work_center_id)
                  .map((wc) => (
                    <label className="rte-checkbox" key={wc.id}>
                      <input
                        type="checkbox"
                        checked={draft.alternate_work_center_ids.includes(wc.id)}
                        disabled={readOnly}
                        onChange={() => {
                          toggleAlternate(wc.id);
                        }}
                      />
                      <span>
                        {wc.code} · {workCenterLabel(wc, lang)}
                      </span>
                    </label>
                  ))}
              </div>
            )}
          </fieldset>

          <label className="rte-field">
            <span>{t("catalogue.routing.fields.qcControlPoint")}</span>
            <input
              type="text"
              value={draft.qc_control_point_id ?? ""}
              readOnly={readOnly}
              placeholder={t("catalogue.routing.fields.qcControlPointPlaceholder")}
              onChange={(e) => {
                patch({ qc_control_point_id: e.target.value === "" ? null : e.target.value });
              }}
            />
            <span className="rte-hint">{t("catalogue.routing.fields.qcControlPointHint")}</span>
          </label>

          <fieldset className="rte-field rte-toggles">
            <legend>{t("catalogue.routing.fields.captureGroup")}</legend>
            <label className="rte-checkbox">
              <input
                type="checkbox"
                checked={draft.requires_measured_capture}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ requires_measured_capture: e.target.checked });
                }}
              />
              <span>{t("catalogue.routing.fields.requiresMeasuredCapture")}</span>
            </label>
            <label className="rte-checkbox">
              <input
                type="checkbox"
                checked={draft.requires_serial_capture}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ requires_serial_capture: e.target.checked });
                }}
              />
              <span>{t("catalogue.routing.fields.requiresSerialCapture")}</span>
            </label>
            <label className="rte-checkbox">
              <input
                type="checkbox"
                checked={draft.backflush}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ backflush: e.target.checked });
                }}
              />
              <span>{t("catalogue.routing.fields.backflush")}</span>
            </label>
            <label className="rte-checkbox">
              <input
                type="checkbox"
                checked={draft.can_overlap}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ can_overlap: e.target.checked });
                }}
              />
              <span>{t("catalogue.routing.fields.canOverlap")}</span>
            </label>
          </fieldset>

          <div className="rte-field-row">
            <label className="rte-field rte-checkbox">
              <input
                type="checkbox"
                checked={draft.is_outsourced}
                disabled={readOnly}
                onChange={(e) => {
                  patch({ is_outsourced: e.target.checked });
                }}
              />
              <span>{t("catalogue.routing.fields.outsourced")}</span>
            </label>
            <label className="rte-field">
              <span>{t("catalogue.routing.fields.minCrew")}</span>
              <input
                type="number"
                min={1}
                step={1}
                value={String(draft.min_crew)}
                readOnly={readOnly}
                onChange={(e) => {
                  patch({ min_crew: Math.max(1, Math.trunc(numOr(e.target.value, 1))) });
                }}
              />
            </label>
          </div>

          {draft.is_outsourced ? (
            <label className="rte-field">
              <span>{t("catalogue.routing.fields.outsourceCode")}</span>
              <input
                type="text"
                value={draft.outsource_code ?? ""}
                readOnly={readOnly}
                placeholder={t("catalogue.routing.fields.outsourceCodePlaceholder")}
                onChange={(e) => {
                  patch({ outsource_code: e.target.value === "" ? null : e.target.value });
                }}
              />
              {hasError("outsourceCodeRequired") ? (
                <span className="rte-field-error">
                  {t("catalogue.routing.errors.outsourceCodeRequired")}
                </span>
              ) : null}
            </label>
          ) : null}

          <div className="rte-field">
            <span className="rte-field-label">{t("catalogue.routing.fields.instructions")}</span>
            <InstructionsEditor
              doc={draft.instructions}
              readOnly={readOnly}
              onChange={(next: InstructionsDoc) => {
                patch({ instructions: next });
              }}
            />
          </div>
        </div>

        <footer className="cat-modal-foot">
          {onDelete !== undefined && !readOnly ? (
            <Button variant="danger" onClick={onDelete}>
              {t("catalogue.routing.drawer.delete")}
            </Button>
          ) : (
            <span />
          )}
          <div className="cat-modal-foot-right">
            <Button onClick={onCancel}>
              {readOnly
                ? t("catalogue.routing.drawer.close")
                : t("catalogue.routing.drawer.cancel")}
            </Button>
            {!readOnly ? (
              <Button variant="primary" onClick={handleApply}>
                {t("catalogue.routing.drawer.apply")}
              </Button>
            ) : null}
          </div>
        </footer>
      </aside>
    </div>
  );
}
